import React from 'react';
import { format, startOfYear, subYears, endOfYear } from 'date-fns';
import { Link, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Text, Icon } from '@chakra-ui/react';

import { formatSI, findSiDefinition, singleMonthLetter, formatDollars } from '../../../../modules/format';
import BaseChart from '../../treasury/BaseChart/BaseChart';
import styles from './Claims.module.css';
import { calculateRoundedMax } from '../../../../modules/data/rounded-max';
import { ChevronRight } from '../../../../shared/Icons';
import yearOnYear, { getUniqueYears } from '../../../../modules/data/year-on-year';
import {
  AMOUNT_OPTIONS,
  STATUS_OPTIONS,
  TIME_RANGE_OPTIONS,
} from '../../../../slices/quality-details-claims/qualityDetailsClaimsConfig';
import {
  setQualityClaimsAmount,
  setQualityClaimsCount,
  setQualityClaimsRegion,
  setQualityClaimsSelectedPackerPlantId,
  setQualityClaimsStatus,
  setQualityClaimsTimeRange,
  updateQualityClaimsTimePeriod,
} from '../../../../slices/quality-details-claims/qualityDetailsClaimsReducers';

const ClaimAmount = props => {
  const { data, singlePackerPlant, selectedRegion } = props;
  const { market: marketParam } = useParams();
  const absoluteMax = Math.max(...data.map(d => Math.abs(d.value)));
  const siDefinition = findSiDefinition(absoluteMax);

  const dispatch = useDispatch();
  const handleNavigationToDetails = React.useCallback(() => {
    if (singlePackerPlant) {
      dispatch(setQualityClaimsSelectedPackerPlantId(singlePackerPlant.id));
      dispatch(setQualityClaimsRegion(singlePackerPlant.country));
    } else {
      dispatch(setQualityClaimsRegion(selectedRegion));
    }
    dispatch(setQualityClaimsStatus(STATUS_OPTIONS.SUBMITTED));
    dispatch(setQualityClaimsCount(null));
    dispatch(setQualityClaimsAmount(AMOUNT_OPTIONS.TOTAL));
    const now = new Date();
    dispatch(updateQualityClaimsTimePeriod([startOfYear(subYears(now, 3)), endOfYear(now)]));
    dispatch(setQualityClaimsTimeRange(TIME_RANGE_OPTIONS.YOY));
  }, [dispatch, selectedRegion, singlePackerPlant]);

  return (
    <div className={styles.QualityClaims_claimAmount}>
      <div className={styles.QualityClaims_incidentsTitle}>
        <Link
          to={`/analytic-dashboard/quality/${marketParam}/claims-details`}
          onClick={() => handleNavigationToDetails()}
        >
          <Text className={styles.QualityClaims_label}>
            claim amount&nbsp;
            <Icon style={{ position: 'relative', width: 14 }}>
              <ChevronRight />
            </Icon>
          </Text>
        </Link>
      </div>
      {data && (
        <BaseChart
          barData={yearOnYear(
            data.map(x => ({
              interval: format(new Date(x.date).getTime(), 'MMM yyyy'),
              value: x.value,
            }))
          )}
          columns={getUniqueYears(data.map(x => format(new Date(x.date), 'MMM yyyy')))}
          colors={{ value: '#959BB5' }}
          splitIndex={-1}
          formatValues={value => formatSI(1, 1, siDefinition)(value)}
          formatIntervals={singleMonthLetter}
          formatTooltip={v => formatDollars(2)(v)}
          noCurrentLine
          noLegend
          ticksNumber={3}
          minMaxGenerator={maxData => {
            return {
              maxVal: calculateRoundedMax(maxData),
              minVal: 0,
            };
          }}
          isLineChart
          withSmoothing={false}
          isYearOnYear
          isSmall
        />
      )}
    </div>
  );
};

export default ClaimAmount;

ClaimAmount.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
  singlePackerPlant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
  }),
  selectedRegion: PropTypes.string,
};
